import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const faqs = [
    {
      question: "Why should we fund FIGO instead of existing vendors or in-house systems?",
      answer: "Most existing systems digitize isolated processes; they do not fix fragmentation across payments, verification, reporting, and accountability. FIGO integrates these into one operational layer. This reduces leakage, duplication, and manual reconciliation. Funding FIGO is not funding another tool — it is funding system-level efficiency and long-term cost reduction."
    },
    {
      question: "How do we know this won't fail after pilot funding ends?",
      answer: "FIGO is designed for sustainability. We embed revenue-generating enterprise use cases (payments, BNPL, merchant services) alongside donor-funded programs. This blended model ensures the platform remains operational even after grant cycles end, reducing dependency on perpetual donor funding."
    },
    {
      question: "How does FIGO address corruption and fund leakage?",
      answer: "FIGO enforces end-to-end traceability: every transaction, beneficiary action, and disbursement is logged and auditable in real time. This replaces paper trails and delayed reports with live dashboards. Leakage becomes visible immediately, not months later."
    },
    {
      question: "Can FIGO work within government regulations and data laws?",
      answer: "Yes. FIGO is built with compliance-by-design. We align with national data protection regulations, financial reporting standards, and audit requirements. Deployment models can be cloud, hybrid, or government-hosted depending on policy constraints."
    },
    {
      question: "How scalable is FIGO across states, regions, or countries?",
      answer: "FIGO is modular. Governments can start with one program or ministry and scale horizontally without rebuilding infrastructure. This allows phased national rollouts and cross-border adaptation with localized policy rules."
    },
    {
      question: "What evidence do you have that this will improve outcomes, not just reporting?",
      answer: "Operational efficiency directly impacts outcomes. Faster payments reduce dropout rates, verified beneficiaries improve targeting accuracy, and real-time data enables faster corrective action. FIGO links operations to measurable program KPIs, not vanity metrics."
    },
    {
      question: "How will this integrate with systems we already use?",
      answer: "FIGO is API-first. We integrate with existing HR systems, payment providers, national ID databases, and reporting tools. Governments do not need to rip and replace; FIGO sits above existing infrastructure and connects."
    },
    {
      question: "What happens if internet access is unreliable?",
      answer: "FIGO supports offline-first workflows with secure synchronization once connectivity is restored. This is critical for rural and low-infrastructure environments and has been factored into the system design from day one."
    },
    {
      question: "Who owns the data?",
      answer: "The implementing government or donor owns the data. FIGO acts as a processor, not a data owner. Data governance, access controls, and retention policies are contractually enforced."
    },
    {
      question: "How do you ensure local capacity and avoid vendor lock-in?",
      answer: "FIGO includes structured knowledge transfer, local partner onboarding, and documentation. Governments can operate and extend the platform independently if required. Our goal is capacity-building, not dependency."
    },
    {
      question: "What are the main risks and how are they mitigated?",
      answer: "Key risks include change management, adoption, and interoperability. These are mitigated through phased rollouts, stakeholder training, and early technical integration assessments."
    },
    {
      question: "How will success be measured?",
      answer: "Success is measured across three layers: operational efficiency (cost and time savings), governance (auditability and compliance), and impact (beneficiary outcomes). These metrics are agreed upfront and reported transparently."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section style={{
      padding: isMobile ? '3rem 4%' : '6rem 5%',
      background: 'var(--bg-secondary)'
    }}>
      <div style={{
        maxWidth: '900px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: isMobile ? '2rem' : '3rem' }}>
          <span style={{
            display: 'inline-block',
            padding: isMobile ? '0.4rem 1rem' : '0.5rem 1.25rem',
            background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.15) 0%, rgba(13, 148, 136, 0.15) 100%)',
            border: '1px solid rgba(37, 99, 235, 0.25)',
            borderRadius: '50px',
            color: 'var(--figo-primary)',
            fontSize: isMobile ? '0.75rem' : '0.85rem',
            fontWeight: '600',
            marginBottom: '1rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            FAQ
          </span>

          <h2 style={{
            fontSize: isMobile ? '1.75rem' : 'clamp(2rem, 5vw, 2.5rem)',
            fontWeight: '700',
            color: 'var(--text-primary)',
            marginBottom: '0.75rem'
          }}>
            Frequently Asked Questions
          </h2>

          <p style={{
            fontSize: isMobile ? '0.95rem' : '1.1rem',
            color: 'var(--text-secondary)',
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: '1.7'
          }}>
            Get answers to common questions about FIGO Digital Services
          </p>
        </div>

        {/* FAQ List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '0.75rem' : '1rem' }}>
          {faqs.slice(0, showAll ? faqs.length : 5).map((faq, index) => (
            <div
              key={index}
              style={{
                background: 'var(--card-bg, rgba(255, 255, 255, 0.03))',
                border: `1px solid ${openIndex === index ? 'var(--figo-primary)' : 'var(--card-border, rgba(255, 255, 255, 0.08))'}`,
                borderRadius: isMobile ? '12px' : '16px',
                overflow: 'hidden',
                transition: 'borderColor 0.3s ease'
              }}
            >
              {/* Question */}
              <button
                onClick={() => toggleFAQ(index)}
                style={{
                  width: '100%',
                  padding: isMobile ? '1rem' : '1.5rem',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: isMobile ? '0.75rem' : '1rem',
                  textAlign: 'left'
                }}
              >
                <span style={{
                  fontSize: isMobile ? '0.9rem' : '1.05rem',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  lineHeight: '1.5'
                }}>
                  {faq.question}
                </span>
                <span style={{
                  width: isMobile ? '28px' : '32px',
                  height: isMobile ? '28px' : '32px',
                  borderRadius: '50%',
                  background: openIndex === index
                    ? 'linear-gradient(135deg, #2563eb 0%, #0d9488 100%)'
                    : 'rgba(255, 255, 255, 0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'background 0.3s ease, color 0.3s ease',
                  color: openIndex === index ? 'white' : 'var(--text-secondary)',
                  fontSize: isMobile ? '1.1rem' : '1.25rem',
                  fontWeight: '300',
                  marginTop: '2px'
                }}>
                  {openIndex === index ? '−' : '+'}
                </span>
              </button>

              {/* Answer */}
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ overflow: 'hidden' }}
                  >
                    <div style={{
                      padding: isMobile ? '0 1rem 1rem' : '0 1.5rem 1.5rem',
                      borderTop: '1px solid var(--card-border, rgba(255, 255, 255, 0.08))',
                      marginTop: '-0.5rem',
                      paddingTop: '1rem'
                    }}>
                      <p style={{
                        fontSize: isMobile ? '0.875rem' : '0.95rem',
                        color: 'var(--text-secondary)',
                        lineHeight: '1.8',
                        margin: 0
                      }}>
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        {/* Show All Button */}
        {!showAll && faqs.length > 5 && (
          <div style={{ textAlign: 'center', marginTop: isMobile ? '1.5rem' : '2rem' }}>
            <button
              onClick={() => setShowAll(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: isMobile ? '0.875rem 1.75rem' : '1rem 2rem',
                background: 'transparent',
                border: '2px solid var(--figo-primary)',
                borderRadius: '50px',
                color: 'var(--figo-primary)',
                fontSize: isMobile ? '0.9rem' : '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--figo-primary)';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--figo-primary)';
              }}
            >
              See all {faqs.length} questions <span>→</span>
            </button>
          </div>
        )}

        {/* Bottom CTA */}
        {/* <div style={{
            textAlign: 'center',
            marginTop: isMobile ? '2rem' : '3rem',
            padding: isMobile ? '1.5rem 1rem' : '2rem',
            background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(13, 148, 136, 0.1) 100%)',
            borderRadius: isMobile ? '16px' : '20px',
            border: '1px solid rgba(37, 99, 235, 0.2)'
          }}
        >
          <p style={{
            color: 'var(--text-primary)',
            fontSize: isMobile ? '1rem' : '1.1rem',
            marginBottom: '1rem'
          }}>
            Still have questions?
          </p>
          <a
            href="/contact"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: isMobile ? '0.75rem 1.5rem' : '0.875rem 1.75rem',
              background: 'linear-gradient(135deg, #2563eb 0%, #0d9488 100%)',
              borderRadius: '50px',
              color: 'white',
              fontSize: isMobile ? '0.9rem' : '0.95rem',
              fontWeight: '600',
              textDecoration: 'none',
              transition: 'transform 0.3s ease, boxShadow 0.3s ease',
              boxShadow: '0 4px 10px rgba(37, 99, 235, 0.25)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 12px rgba(37, 99, 235, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 10px rgba(37, 99, 235, 0.25)';
            }}
          >
            Contact Us →
          </a>
        </div> */}
      </div>
    </section>
  );
};

export default FAQ;
